/**
 * Utilidades para exportar datos a CSV
 */

export interface CSVColumn {
    key: string;
    label: string;
    formatter?: (value: any, item?: any) => string;
}

/**
 * Convierte un array de objetos a formato CSV
 * @param data - Array de objetos a convertir
 * @param columns - Configuración de columnas
 * @param filename - Nombre del archivo (sin extensión)
 */
export const exportToCSV = <T>(
    data: T[],
    columns: CSVColumn[],
    filename: string = 'export'
): void => {
    if (!data || data.length === 0) {
        console.warn('No hay datos para exportar');
        return;
    }

    // Crear headers del CSV
    const headers = columns.map(col => col.label);
    
    // Crear filas del CSV
    const rows = data.map(item => {
        return columns.map(col => {
            const value = getNestedValue(item, col.key);
            
            // Aplicar formatter si existe (pasando tanto el valor como el item completo)
            const formattedValue = col.formatter ? col.formatter(value, item) : value;
            
            // Limpiar y escapar el valor para CSV
            return escapeCSVValue(formattedValue);
        });
    });

    // Combinar headers y rows
    const csvContent = [headers, ...rows]
        .map(row => row.join(','))
        .join('\n');

    // Crear y descargar el archivo
    downloadCSV(csvContent, filename);
};

/**
 * Obtiene un valor anidado de un objeto usando notación de punto
 * Ejemplo: getNestedValue(obj, 'user.name')
 */
const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : '';
    }, obj);
};

/**
 * Escapa valores para CSV (maneja comillas, saltos de línea, etc.)
 */
const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) {
        return '';
    }

    const stringValue = String(value);
    
    // Si contiene comas, comillas o saltos de línea, envolver en comillas
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        // Escapar comillas dobles duplicándolas
        const escapedValue = stringValue.replace(/"/g, '""');
        return `"${escapedValue}"`;
    }

    return stringValue;
};

/**
 * Descarga el contenido CSV como archivo
 */
const downloadCSV = (csvContent: string, filename: string): void => {
    // Agregar BOM para UTF-8 (para que Excel abra correctamente los acentos)
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Crear blob con encoding UTF-8
    const blob = new Blob([csvWithBOM], { 
        type: 'text/csv;charset=utf-8;' 
    });

    // Crear enlace de descarga
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    
    // Agregar al DOM, hacer click y remover
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Limpiar URL object
    URL.revokeObjectURL(url);
};

/**
 * Formateadores comunes para diferentes tipos de datos
 */
export const csvFormatters = {
    /**
     * Formatea fechas a formato dd/MM/yyyy
     */
    date: (value: any): string => {
        if (!value) return '';
        try {
            const date = new Date(value);
            return date.toLocaleDateString('es-ES');
        } catch {
            return String(value);
        }
    },

    /**
     * Formatea booleanos a Sí/No
     */
    boolean: (value: any): string => {
        if (value === true) return 'Sí';
        if (value === false) return 'No';
        return '';
    },

    /**
     * Formatea números con separador de miles
     */
    number: (value: any): string => {
        if (typeof value !== 'number') return String(value || '');
        return value.toLocaleString('es-ES');
    },

    /**
     * Limpia texto removiendo saltos de línea y espacios extra
     */
    cleanText: (value: any): string => {
        if (!value) return '';
        return String(value)
            .replace(/\n/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }
};
